import { Link, useNavigate } from '@tanstack/react-router';
import { MailCheck } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/shared/ui/alert';

import { googleSignInEnabled } from '../auth.flags';
import { authService } from '../auth.service';
import { AuthLayout } from '../components/auth-layout';
import { GoogleButton } from '../components/google-button';
import { SignUpForm } from '../components/sign-up-form';

import type { SignUpInput } from '../auth.schemas';

export function SignUpPage() {
  const navigate = useNavigate();
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [oauthPending, setOauthPending] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);

  async function handleSubmit(input: SignUpInput): Promise<string | null> {
    const { data, error } = await authService.signUp(input);
    if (error) return error.message;
    // With email confirmation on, Supabase returns a user but no session until the link is clicked.
    if (data.session) {
      await navigate({ to: '/' });
    } else {
      setPendingEmail(input.email);
    }
    return null;
  }

  async function handleGoogle() {
    if (oauthPending) return;
    setOauthPending(true);
    setOauthError(null);
    try {
      const { error } = await authService.signInWithGoogle();
      if (error) {
        setOauthError(error.message);
        setOauthPending(false);
      }
      // Keep the loader visible while the successful redirect leaves this page.
    } catch {
      setOauthError('Could not connect to Google. Please try again.');
      setOauthPending(false);
    }
  }

  if (pendingEmail) {
    return (
      <AuthLayout>
        <MailCheck className="auth-confirmation-icon size-6" aria-hidden="true" />
        <h1 className="auth-form-title">Check your inbox</h1>
        <p className="auth-form-description">
          We sent a confirmation link to{' '}
          <span className="auth-confirmation-email">{pendingEmail}</span>. Open it to activate your
          account, then sign in.
        </p>
        <p className="auth-account-link">
          <Link to="/sign-in">Back to sign in</Link>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h1 className="auth-form-title">Create your account</h1>
      <p className="auth-form-description">
        Create an account to begin your AI engineering residency.
      </p>

      <div>
        {oauthError ? (
          <div className="mb-4">
            <Alert>{oauthError}</Alert>
          </div>
        ) : null}
        {googleSignInEnabled ? (
          <>
            <GoogleButton loading={oauthPending} onClick={() => void handleGoogle()} />
            <div className="auth-divider">or continue with email</div>
          </>
        ) : null}
        <SignUpForm onSubmit={handleSubmit} />
      </div>

      <p className="auth-account-link">
        Already have an account?
        <Link to="/sign-in">Sign in</Link>
      </p>
    </AuthLayout>
  );
}
